import { faroCoreApiClient } from "@api/faro-core-api-client";
import { CompanyId } from "@custom-types/base-types";
import { exponentialBackOff, retry } from "@faro-lotv/foundation";
import {
  IntegrationCompany,
  IntegrationProject,
  ProjectIntegrationsMap,
  ServiceLegacyMap,
} from "@services/integrations-service/integrations-service-types";
import { CoreApiClient, FaroCoreApiTypes } from "@stellar/core-api-client";
import { HttpUtils } from "@stellar/utils";

interface Props {
  /** Callback to re-authorize the integration */
  reauthorize: (integrationId: FaroCoreApiTypes.IntegrationId) => Promise<void>;
}

/** Abstract class to manage a single 3rd party integration */
export abstract class BaseService {
  protected coreApiClient: CoreApiClient = faroCoreApiClient;
  #reauthorize: (
    integrationId: FaroCoreApiTypes.IntegrationId
  ) => Promise<void>;
  protected abstract integrationId: FaroCoreApiTypes.IntegrationId;

  constructor({ reauthorize }: Props) {
    this.#reauthorize = reauthorize;
  }

  /**
   * @returns the IntegrationCompany entities of the user
   * @throws {Error} if it fails to get the entities
   */
  public abstract getIntegrationCompanies(): Promise<IntegrationCompany[]>;

  /**
   * @returns the IntegrationProject entities for a given integration company
   * @throws {Error} if it fails to get the entities
   * @param companyId ID of the company
   */
  public abstract getIntegrationProjects(
    companyId: CompanyId
  ): Promise<IntegrationProject[]>;

  /**
   * Handles errors specific to this integration service.
   * @param error Error to handle
   */
  protected abstract handleIntegrationError(error: HttpUtils.FetchError): Promise<void>;

  /**
   * Checks if the integration still has the Legacy (HB) project connection data format. If that's the
   * case it then updates the data to the Sphere XG format.
   * @returns The project integrations map with the updated data format
   * @param data Project integrations map
   * @throws {Error} if it fails to update the data format
   */
  public abstract updateDataFormat(
    data: ServiceLegacyMap
  ): Promise<ProjectIntegrationsMap>;

  /**
   * @returns Whether there is a project connected in the legacy (HB) data format.
   * @param data Project integrations map
   */
  public abstract isConnectedInLegacyFormat(
    data: ProjectIntegrationsMap
  ): data is ServiceLegacyMap;

  /**
   * @returns Whether there is a project connected in the XG data connection format.
   * @param projectIntegrationsMap Project integrations map
   */
  public abstract isConnectedInXGFormat(
    projectIntegrationsMap: ProjectIntegrationsMap
  ): boolean;

  /**
   * @returns Whether there is a project connected either in legacy or XG data format.
   * @param projectIntegrationsMap Project integrations map
   */
  public abstract isConnected(
    projectIntegrationsMap: ProjectIntegrationsMap
  ): boolean;

  /**
   * Issues a request to the integration API (Ex: Autodesk or Procore API) through the Core API
   * If the request fails it retries 3 times and it also attempts to handle common and
   * integration specific errors before attempting to issue the request again.
   * @returns the response of the issued request
   * @throws {Error} if the request fails and the error could not be handled
   * @param proxyRequest of the endpoint to issue the request
   */
  protected async request<T>(proxyRequest: () => Promise<T>): Promise<T> {
    try {
      return await retry(proxyRequest, {
        max: 3,
        delay: exponentialBackOff,
      });
    } catch (error) {
      await this.handleError(error);
      return await retry(proxyRequest, {
        max: 3,
        delay: exponentialBackOff,
      });
    }
  }

  /**
   * It first attempts to refresh the token. If the token refresh fails it requests
   * the user to re-authorize the integration.
   */
  public async handleTokenUpdate(): Promise<void> {
    try {
      await this.refreshIntegrationToken();
    } catch {
      await this.#reauthorize(this.integrationId);
    }
  }

  /**
   * Handles errors originating from the CoreAPI proxy backend for requests issued to the Procore backend.
   * - Validates that the error has the type of a CoreAPI error. If not it throws the error as it is.
   * - Handles errors that are specific to the 3rd party integration.
   * @param error Error to handle
   */
  private async handleError(error: unknown): Promise<void> {
    if (!HttpUtils.isFetchError(error)) {
      throw error;
    }

    await this.handleIntegrationError(error);
  }

  /**
   * Issues a request to refresh the integration token
   * @returns the refreshed integration token
   * @throws {HttpUtils.FetchError} if it fails to refresh the integration token
   */
  private async refreshIntegrationToken(): Promise<FaroCoreApiTypes.IntegrationToken> {
    return await this.coreApiClient.integration.refreshIntegrationToken(
      this.integrationId
    );
  }
}
